/** Possession des ressources, y compris celles créées après un démontage. */
export class ResourceScope {
  #disposed = false;
  #cleanup: (() => void)[] = [];
  get disposed(): boolean { return this.#disposed; }
  own(cleanup: () => void): void {
    if (this.#disposed) cleanup();
    else this.#cleanup.push(cleanup);
  }
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    for (const cleanup of this.#cleanup.splice(0).reverse()) {
      try { cleanup(); } catch (error) { console.error(error); }
    }
  }
}

export type MountStatus = "loading" | "ready" | "failed" | "disposed";

/** Commandes coalescées pendant le chargement, refusées après échec ou démontage. */
export function mountResource<T>(
  attach: (scope: ResourceScope) => Promise<T | undefined>,
  onFailure: (error: unknown) => void,
): {
  readonly ready: Promise<boolean>;
  readonly status: () => MountStatus;
  readonly command: (key: string, command: (api: T) => void) => void;
  readonly detach: () => void;
} {
  const scope = new ResourceScope();
  const pending = new Map<string, (api: T) => void>();
  let status: MountStatus = "loading";
  let api: T | undefined;
  const ready = Promise.resolve().then(() => attach(scope)).then((mounted) => {
    if (scope.disposed) return false;
    if (mounted === undefined) {
      status = "failed";
      pending.clear();
      scope.dispose();
      return false;
    }
    api = mounted;
    status = "ready";
    const commands = [...pending.values()];
    pending.clear();
    for (const command of commands) command(mounted);
    return true;
  }).catch((error: unknown) => {
    pending.clear();
    api = undefined;
    if (status !== "disposed") {
      status = "failed";
      scope.dispose();
      onFailure(error);
    }
    return false;
  });
  return {
    ready,
    status: () => status,
    command: (key, command) => {
      if (status === "ready" && api !== undefined) command(api);
      else if (status === "loading") { pending.delete(key); pending.set(key, command); }
    },
    detach: () => {
      status = "disposed";
      api = undefined;
      pending.clear();
      scope.dispose();
    },
  };
}
